import { useState, useEffect, useRef, FormEvent } from 'react';
import { useAuth } from '../AuthContext';
import { getSession, submitSmsCode, submitPayment, cancelSession, Session } from '../api';

const STATUS_MESSAGES: Record<string, { title: string; description: string; color: string }> = {
  registered: {
    title: 'Signed Up',
    description: 'You are registered for this drop. The bot will run automatically at 11:00 AM PT.',
    color: '#3498db',
  },
  bot_running: {
    title: 'Bot Running',
    description: 'The bot is active and waiting for the noon drop...',
    color: '#f39c12',
  },
  awaiting_sms: {
    title: 'Enter SMS Code',
    description: 'Check your phone for a verification code from Hotplate.',
    color: '#e74c3c',
  },
  awaiting_payment: {
    title: 'Enter Payment',
    description: 'Your pizza is reserved! Enter your card details to complete the order.',
    color: '#e74c3c',
  },
  confirmed: {
    title: 'Order Confirmed!',
    description: 'You scored a pizza! Check your email for pickup details.',
    color: '#27ae60',
  },
  failed: {
    title: 'Order Failed',
    description: 'Something went wrong with the order.',
    color: '#e74c3c',
  },
};

function getTokenFromPath(): string {
  const parts = window.location.pathname.split('/');
  return parts[parts.length - 1] || '';
}

export default function DropSessionPage() {
  const { user } = useAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // SMS form state
  const [smsCode, setSmsCode] = useState('');
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [smsError, setSmsError] = useState('');

  // Payment form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardZip, setCardZip] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payError, setPayError] = useState('');

  const token = getTokenFromPath();

  useEffect(() => {
    loadSession();

    // Poll every 2 seconds
    pollRef.current = setInterval(loadSession, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  async function loadSession() {
    const { data, error: err } = await getSession(token);
    if (data) {
      setSession(data);
      setLoading(false);

      // Stop polling on terminal states
      if (data.status === 'confirmed' || data.status === 'failed') {
        if (pollRef.current) clearInterval(pollRef.current);
      }
    } else if (err) {
      setError(err);
      setLoading(false);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }

  async function handleSmsSubmit(e: FormEvent) {
    e.preventDefault();
    setSmsError('');
    setSmsSubmitting(true);

    const { error: err } = await submitSmsCode(token, smsCode);
    setSmsSubmitting(false);

    if (err) {
      setSmsError(err);
    } else {
      setSmsCode('');
    }
  }

  async function handlePaymentSubmit(e: FormEvent) {
    e.preventDefault();
    setPayError('');
    setPaySubmitting(true);

    const { error: err } = await submitPayment(token, cardNumber, cardExpiry, cardCvc, cardZip);
    setPaySubmitting(false);

    if (err) {
      setPayError(err);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div style={{ textAlign: 'center', marginTop: 80 }}>Loading...</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 500, margin: '80px auto', textAlign: 'center' }}>
          <h2>Session Not Found</h2>
          <p>{error || 'This drop session does not exist or you do not have access.'}</p>
          <a href="/">Back to Dashboard</a>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_MESSAGES[session.status] || STATUS_MESSAGES.failed;

  return (
    <div className="page">
      <div style={{ maxWidth: 500, margin: '40px auto' }}>
        <a href="/" style={{ color: '#666', textDecoration: 'none', marginBottom: 16, display: 'block' }}>
          &larr; Back to Dashboard
        </a>

        <div className="card">
          <div style={{
            background: statusInfo.color,
            color: 'white',
            padding: '20px 24px',
            margin: '-20px -20px 20px -20px',
            borderRadius: '8px 8px 0 0',
          }}>
            <h2 style={{ margin: 0 }}>{statusInfo.title}</h2>
            <p style={{ margin: '8px 0 0', opacity: 0.9 }}>{statusInfo.description}</p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Drop Date</span>
              <strong>{session.dropDate}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Status</span>
              <strong>{session.status}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666' }}>Last Updated</span>
              <span>{new Date(session.updatedAt).toLocaleTimeString()}</span>
            </div>
          </div>

          {/* SMS Code Form */}
          {session.status === 'awaiting_sms' && (
            <div style={{ marginTop: 16 }}>
              <h3>Enter Verification Code</h3>
              <p style={{ color: '#666', fontSize: 14 }}>
                Check your phone for a text from Hotplate with a verification code.
              </p>
              {session.checkoutUrl && (
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#e8f5e9', borderRadius: 8, border: '1px solid #a5d6a7' }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Or complete checkout directly:</p>
                  <a
                    href={session.checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#2e7d32', fontWeight: 500, wordBreak: 'break-all' }}
                  >
                    {session.checkoutUrl}
                  </a>
                </div>
              )}
              <form onSubmit={handleSmsSubmit}>
                <div className="field">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={smsCode}
                    onChange={e => setSmsCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter code"
                    maxLength={6}
                    autoFocus
                    style={{ fontSize: 24, textAlign: 'center', letterSpacing: 8, fontFamily: 'monospace' }}
                  />
                </div>
                {smsError && <div className="error">{smsError}</div>}
                <button type="submit" className="btn-primary" disabled={smsSubmitting || smsCode.length < 4}>
                  {smsSubmitting ? 'Submitting...' : 'Submit Code'}
                </button>
              </form>
            </div>
          )}

          {/* Payment Form */}
          {session.status === 'awaiting_payment' && (
            <div style={{ marginTop: 16 }}>
              <h3>Payment Details</h3>
              <p style={{ color: '#666', fontSize: 14 }}>
                Enter your card details. They will be used once and immediately deleted.
              </p>
              <form onSubmit={handlePaymentSubmit}>
                <div className="field">
                  <label htmlFor="cardNumber">Card Number</label>
                  <input
                    id="cardNumber"
                    type="text"
                    inputMode="numeric"
                    value={cardNumber}
                    onChange={e => setCardNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="4242424242424242"
                    maxLength={16}
                    autoFocus
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="field">
                    <label htmlFor="cardExpiry">Expiry</label>
                    <input
                      id="cardExpiry"
                      type="text"
                      value={cardExpiry}
                      onChange={e => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.length > 2) val = val.slice(0, 2) + '/' + val.slice(2, 4);
                        setCardExpiry(val);
                      }}
                      placeholder="MM/YY"
                      maxLength={5}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="cardCvc">CVC</label>
                    <input
                      id="cardCvc"
                      type="text"
                      inputMode="numeric"
                      value={cardCvc}
                      onChange={e => setCardCvc(e.target.value.replace(/\D/g, ''))}
                      placeholder="123"
                      maxLength={4}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="cardZip">ZIP</label>
                    <input
                      id="cardZip"
                      type="text"
                      inputMode="numeric"
                      value={cardZip}
                      onChange={e => setCardZip(e.target.value.replace(/\D/g, ''))}
                      placeholder="98101"
                      maxLength={5}
                    />
                  </div>
                </div>
                {payError && <div className="error">{payError}</div>}
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={paySubmitting || cardNumber.length < 13 || cardCvc.length < 3}
                >
                  {paySubmitting ? 'Submitting...' : 'Submit Payment'}
                </button>
              </form>
            </div>
          )}

          {/* Confirmed */}
          {session.status === 'confirmed' && session.orderNumber && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <p style={{ fontSize: 18 }}>Order #{session.orderNumber}</p>
            </div>
          )}

          {/* Failed */}
          {session.status === 'failed' && session.error && (
            <div className="error" style={{ marginTop: 16 }}>
              {session.error}
            </div>
          )}

          {/* Dismiss button for non-confirmed sessions */}
          {session.status !== 'confirmed' && session.status !== 'bot_running' && (
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <button
                className="btn-secondary"
                style={{ color: '#c62828' }}
                onClick={async () => {
                  if (!confirm('Remove this session?')) return;
                  const { error: err } = await cancelSession(token);
                  if (err) {
                    setError(err);
                  } else {
                    window.location.href = '/';
                  }
                }}
              >
                {session.status === 'registered' ? 'Cancel Registration' : 'Dismiss'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
